import { useEffect, useState, SetStateAction, Dispatch } from 'react'
import classNames from 'classnames'

import { GiftingSummaryHeader } from './GiftingSummaryHeader'

import { NumberFormat } from 'utils/index'

import {
  hasPendingBankAccount,
  isPendingManualVerification,
  paymentMethodInfo
} from 'modules/contribution/helpers'
import { getFrequencyLabel } from 'utils/helpers'

import {
  creditCardWarningText,
  isCreditCardAmountTooHigh
} from 'modules/contribution/components/CreditCardLimitWarning'
import {
  amountValue,
  bankAccountValue,
  creditCardValue,
  frequencyValue,
  messageValue,
  guestAcceptedTerms
} from 'modules/contribution/LocalState'
import {
  BankAccount,
  CreditCard,
  CurrentFlow,
  Frequency
} from 'modules/contribution/types'
import {
  getPaymentMethod,
  handleContributionSuccess,
  handleSubmitContribution
} from 'modules/contribution/helpers'
import { useContributionFeesQuery } from 'hooks'
import { Button, ListItem, Icon } from '@collegebacker/backer-ui/ui'
import config from '../../../../config'
import { useCreateFullContributionDraftMutation } from 'hooks/useCreateFullContributionDraftMutation'
import { PaymentFailureContent } from 'modules/contribution/components/PaymentFailureModal'
import { graphQLErrorMessage } from 'utils/validation'
import { isGuest } from 'utils/user'
import { GuestType } from 'modules/global/types'

import styles from './GiftingConfirmation.module.scss'

const CURRENT_STEP = 'confirmation'

const GiftingConfirmation = ({
  fund,
  dispatch,
  user,
  setShowModalNav
}: Props) => {
  const [disabled, setDisabled] = useState(false)
  const [paymentError, setPaymentError] = useState('')

  const amount = amountValue()
  const frequency = frequencyValue()
  const paymentMethod = getPaymentMethod(
    user,
    bankAccountValue,
    creditCardValue
  )
  const { bankAccount, creditCard } = paymentMethod
  const showTerms = !isGuest(user) && !user.hasPassword

  useEffect(() => {
    if (!bankAccount && !creditCard) {
      dispatch({
        type: 'setActiveStep',
        payload: {
          activeStep: 'message'
        }
      })
    }
  }, [paymentMethod])

  const {
    firstLine,
    secondLine,
    editable: paymentMethodEditable,
    type: paymentMethodType
  } = paymentMethodInfo(paymentMethod)

  const { subscriptionFee, processingFee, fullAmount } =
    useContributionFeesQuery({
      paymentMethodType: paymentMethodType!,
      frequency,
      amount,
      fundUuid: fund.uuid
    })
  const creditCardAmountTooHigh = isCreditCardAmountTooHigh({
    paymentMethod,
    amount,
    frequency
  })

  const { createFullContributionDraft } =
    useCreateFullContributionDraftMutation({
      onSuccess: async (draft: {
        amount: number
        currentFlow: CurrentFlow
        fund: { uuid: string }
        user: { uuid: string }
      }) => {
        try {
          await handleContributionSuccess({
            currentStep: CURRENT_STEP,
            draft,
            paymentMethod,
            setDisabled,
            processingFee,
            subscriptionFee,
            event: 'GiftingPageConfirmation'
          })
          messageValue('')
          if (showTerms) guestAcceptedTerms(true)
        } catch (err: any) {
          console.error(err)
          setShowModalNav(false)
          setPaymentError(graphQLErrorMessage(err))
          setDisabled(false)
        }
      },
      onError: (err) => {
        console.error(err)
        setShowModalNav(false)
        setPaymentError(graphQLErrorMessage(err))
        setDisabled(false)
      }
    })

  const monthlyFeeSubtotalLine = subscriptionFee > 0 && (
    <div className={styles.subTotalsLine}>
      <p>
        {[99, 1_99].includes(subscriptionFee)
          ? `${fund.canUpdateFund ? 'Contribution' : 'Gifting'} fee`
          : 'Monthly fee'}
      </p>
      <p>{NumberFormat.currencyFromCents(subscriptionFee, false, true)}</p>
    </div>
  )

  const creditCardFeeSubtotalLine = processingFee > 0 && (
    <div className={styles.subTotalsLine}>
      <p>3% credit card fee</p>
      <p>{NumberFormat.currencyFromCents(processingFee, false, true)}</p>
    </div>
  )

  const subTotals = subscriptionFee > 0 && (
    <div className={styles.subTotals}>
      {monthlyFeeSubtotalLine}
      {creditCardFeeSubtotalLine}
    </div>
  )

  const frequencyLabel = getFrequencyLabel(frequency).toLowerCase()
  const total = (
    <div className={styles.totalLine}>
      <p>Total</p>
      <div>
        <p className={styles.detailsValue}>
          {NumberFormat.currencyFromCents(fullAmount, false, true)}{' '}
          {frequencyLabel !== 'one-time' && frequencyLabel}
        </p>
        {!bankAccount && !subTotals && (
          <p className={styles.greyText}>Card fee included</p>
        )}
      </div>
    </div>
  )

  if (paymentError) {
    return (
      <PaymentFailureContent
        errorMessage={paymentError}
        close={() => {
          setShowModalNav(true)
          setPaymentError('')
        }}
      />
    )
  }

  return (
    <div>
      <GiftingSummaryHeader fund={fund} />
      <div className={styles.body}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2
              className={classNames(
                'typo-app-title-small',
                styles.sectionTitle
              )}
            >
              Payment method
            </h2>
          </div>
          <div className="sectionBody">
            <ListItem
              disabled={!paymentMethodEditable}
              chevron={paymentMethodEditable}
              title={
                <>
                  {firstLine}
                  <br />
                  {secondLine && (
                    <span className="capitalize-first-letter">
                      {secondLine}
                    </span>
                  )}
                </>
              }
              description={renderFeeText({
                paymentMethod,
                amount,
                frequency
              })}
              rightContent={
                <Icon
                  name={
                    paymentMethodType === 'bank-account'
                      ? 'bank-account'
                      : 'credit-card'
                  }
                />
              }
              onClick={() =>
                dispatch({
                  type: 'setActiveStep',
                  payload: {
                    activeStep: 'select-payment-method',
                    previousStep: 'confirmation'
                  }
                })
              }
            />
            <div className={styles.totalContainer}>
              {subTotals}

              {total}
            </div>
          </div>
        </div>

        <div className={styles.btnSection}>
          {showTerms && (
            <p className={styles.termsLinkCopy}>
              By clicking "Contribute," you agree to Backer's{' '}
              <a
                href={`${config.BACKEND_ROOT_URL}/legal#terms`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href={`${config.BACKEND_ROOT_URL}/legal#privacy`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Notice
              </a>
              .
            </p>
          )}
          <Button
            className={styles.cta}
            disabled={disabled || creditCardAmountTooHigh}
            onClick={() =>
              handleSubmitContribution({
                currentStep: CURRENT_STEP,
                disabled,
                fullAmount,
                fund,
                paymentMethod,
                setDisabled,
                user,
                createFullContributionDraft
              })
            }
            label="Contribute"
            type="submit"
          />
        </div>
      </div>
    </div>
  )
}

export function renderFeeText({
  paymentMethod,
  amount,
  frequency
}: {
  paymentMethod: {
    bankAccount: BankAccount | null | undefined
    creditCard: CreditCard | null | undefined
  }
  amount: number
  frequency: Frequency
}) {
  const { bankAccount, creditCard } = paymentMethod

  const bankAccountPending = hasPendingBankAccount(bankAccount)
  const pendingManualVerification = isPendingManualVerification(bankAccount)
  const creditCardAmountTooHigh = isCreditCardAmountTooHigh({
    paymentMethod,
    amount,
    frequency
  })
  const pendingMessage = pendingManualVerification ? (
    <>
      <p>We are verifying your bank account.</p>
      <p>
        Over the next two business days, Plaid will make two small deposits
        between $0.01 and $0.99 directly into your bank account. We will email
        you instructions for how to verify your bank account by providing us
        with these amounts.
      </p>
    </>
  ) : (
    <>
      <p>We are verifying your bank account.</p>
      <p>
        Over the next two business days, Plaid will make two small deposits
        between $0.01 and $0.99 directly into your bank account, which we will
        verify. No action is required from you and we will email you once the
        process is complete.
      </p>
    </>
  )

  if (creditCard) {
    if (creditCardAmountTooHigh) {
      return creditCardWarningText(frequency)
    }
    return 'Credit card (3% fee)'
  } else if (bankAccount) {
    if (bankAccountPending) {
      return pendingMessage
    }
  }
  return 'No credit/debit processing fee'
}

interface Props {
  fund: {
    fundName: string
    fundOwner: {
      firstName: string
      fullName: string
    }
    giftingPage: {
      photoUrl: string
    }
    uuid: string
    canUpdateFund: boolean
  }
  dispatch: (arg: object) => void
  user: {
    bankAccounts: BankAccount[]
    creditCards: CreditCard[]
    defaultPaymentMethodId: string
    defaultPaymentMethodType: string
    email: string
    fullName: string
    hasPassword: boolean
    guest: GuestType
  }
  setShowModalNav: Dispatch<SetStateAction<boolean>>
}

export default GiftingConfirmation
